import React, { useMemo, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import LoginView from '../components/LoginView';
import LandingView from '../components/LandingView';
import PrivacyView from '../components/PrivacyView';
import TermsView from '../components/TermsView';
import AdminLayout from '../modules/admin/AdminLayout';
import { AppShell } from '../shared/layouts/AppShell';
import { AppRouter } from './AppRouter';
import BottomBar from '../components/BottomBar';
import { View } from '../types';
import { WorkspaceSelectionView } from '../components/workspace/WorkspaceSelectionView';
import { OnboardingView } from '../components/onboarding/OnboardingView';
import { ViewRouter, isPlatformView } from './ViewRouter';
import { motion } from 'motion/react';

export type PlatformRoute = 
  | 'landing'
  | 'login'
  | 'privacy'
  | 'terms'
  | 'admin'
  | 'workspace-selection'
  | 'onboarding'
  | 'app';

interface PlatformRouterProps {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  isCommandPaletteOpen: boolean;
  setIsCommandPaletteOpen: (open: boolean) => void;
  isChatOpen: boolean;
  setIsChatOpen: (open: boolean) => void;
  ChatComponent?: React.ReactNode;
}

export function PlatformRouter({
  isSidebarCollapsed,
  toggleSidebar,
  isCommandPaletteOpen,
  setIsCommandPaletteOpen,
  isChatOpen,
  setIsChatOpen,
  ChatComponent,
}: PlatformRouterProps) {
  const { user, isAuthenticated, loading, activeWorkspace, workspaceLoading } = useAuth();
  const { currentView, setCurrentView } = useUI();

  // Determine current platform route
  const platformRoute = useMemo<PlatformRoute>(() => {
    if (loading) {
      return 'login';
    }

    if (isAuthenticated && workspaceLoading) {
      return 'app';
    }

    if (['landing', 'privacy', 'terms'].includes(String(currentView))) {
      return currentView as PlatformRoute;
    }

    if (String(currentView).startsWith('admin')) {
      return 'admin';
    }

    if (!isAuthenticated) {
      return 'login';
    }

    if (!activeWorkspace) {
      return 'workspace-selection';
    }

    const isOnboardingComplete = activeWorkspace?.settings?.onboardingCompleted === true;
    if (!isOnboardingComplete) {
      return 'onboarding';
    }

    return 'app';
  }, [loading, isAuthenticated, currentView, activeWorkspace, workspaceLoading]);

  // Render based on platform route
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#111111]/20 border-t-[#111111] rounded-full animate-spin" />
          <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest">
            Carregando segurança...
          </span>
        </div>
      </div>
    );
  }

  switch (platformRoute) {
    case 'landing':
      return (
        <LandingView onNavigate={(view) => setCurrentView(view)} />
      );

    case 'login':
      return (
        <LoginView 
          onLogin={() => setCurrentView('dashboard')} 
          onNavigate={(view) => setCurrentView(view)} 
        />
      );

    case 'privacy':
      return (
        <PrivacyView 
          onBack={() => setCurrentView(isAuthenticated ? 'dashboard' : 'login')} 
        />
      );

    case 'terms':
      return (
        <TermsView 
          onBack={() => setCurrentView(isAuthenticated ? 'dashboard' : 'login')} 
        />
      );

    case 'admin':
      return (
        <AdminLayout currentView={currentView} setCurrentView={setCurrentView} />
      );

    case 'workspace-selection':
      return (
        <WorkspaceSelectionView onWorkspaceSelected={() => setCurrentView('dashboard')} />
      );

    case 'onboarding':
      return (
        <OnboardingView 
          onComplete={() => {
            // Let the PlatformRouter naturally re-evaluate
            // Since activeWorkspace.settings.onboardingCompleted is now true,
            // the next render will detect this and show the 'app' route
            sessionStorage.setItem('just_finished_onboarding', 'true');
          }}
        />
      );

    case 'app':
      return (
        <AppShell
          currentView={currentView as View}
          setCurrentView={setCurrentView}
          isSidebarCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
        >
          {/* Platform Views (Dashboard, Settings, etc) */}
          {isPlatformView(String(currentView)) ? (
            <ViewRouter view={currentView as View} />
          ) : (
            /* App-specific views */
            <AppRouter currentView={currentView as View} />
          )}

          <BottomBar currentView={currentView as View} setCurrentView={setCurrentView} />

          {/* Chat Component */}
          {ChatComponent && isChatOpen && (
            <motion.div
              initial={{ opacity: 0, y: 15, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed bottom-24 lg:bottom-8 right-6 lg:right-8 z-50 w-[calc(100vw-48px)] sm:w-[380px] md:w-[420px] pointer-events-auto"
            >
              {ChatComponent}
            </motion.div>
          )}
        </AppShell>
      );

    default:
      return (
        <LoginView 
          onLogin={() => setCurrentView('dashboard')} 
          onNavigate={(view) => setCurrentView(view)} 
        />
      );
  }
}
